import axios from 'axios';
import { call, put, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import fileDownload from 'js-file-download';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	getPpwrPackingTypesRequest,
	getPpwrPackingTypesSuccess,
	updatePpwrPackingTypeWeightRequest,
	updatePpwrPackingTypeWeightSuccess,
	updatePpwrPackingTypeEnabledRequest,
	updatePpwrPackingTypeEnabledSuccess,
	uploadPpwrProofDocumentRequest,
	uploadPpwrProofDocumentSuccess,
	deletePpwrProofDocumentRequest,
	deletePpwrProofDocumentSuccess,
	downloadPpwrProofDocumentRequest,
	downloadPpwrProofDocumentSuccess,
	getPpwrCountryExclusionsRequest,
	getPpwrCountryExclusionsSuccess,
	createPpwrCountryExclusionRequest,
	updatePpwrCountryExclusionRequest,
	savePpwrCountryExclusionSuccess,
	getPpwrManualReportingRequest,
	getPpwrManualReportingSuccess,
	upsertPpwrManualReportRequest,
	upsertPpwrManualReportSuccess,
} from './reducer';
import {
	TPpwrCountryExclusion,
	TPpwrCountryExclusionBody,
	TPpwrCountryExclusionsResponsePayload,
	TPpwrDownloadProofDocumentRequest,
	TPpwrManualReportingRequestPayload,
	TPpwrManualReportingResponsePayload,
	TPpwrManualReportRequest,
	TPpwrPackingTypeEnabledRequest,
	TPpwrPackingTypesResponsePayload,
	TPpwrPackingTypeWeightRequest,
	TPpwrProofDocument,
	TPpwrResponseMessage,
	TPpwrUpdateCountryExclusionRequest,
	TPpwrUploadProofDocumentRequest,
} from './types';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

const showRequestError = (error: unknown) => {
	if (axios.isAxiosError(error))
		notificationContainer(
			error?.response?.data?.message ?? error.message ?? 'Request error',
			'error',
		);
};

function* getPpwrPackingTypesWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrPackingTypesResponsePayload = yield call(api.ppwr.getPackingTypes);
		yield put(getPpwrPackingTypesSuccess(response));
	} catch (error) {
		showRequestError(error);
		yield put(getPpwrPackingTypesSuccess(null));
	}
}

function* updatePpwrPackingTypeWeightWorker(action: PayloadAction<TPpwrPackingTypeWeightRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrResponseMessage = yield call(
			api.ppwr.updatePackingTypeWeight,
			action.payload,
		);
		yield put(getPpwrPackingTypesRequest());
		yield put(updatePpwrPackingTypeWeightSuccess(action.payload));
		notificationContainer(response?.message ?? 'Weight updated successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(updatePpwrPackingTypeWeightSuccess(null));
	}
}

function* updatePpwrPackingTypeEnabledWorker(
	action: PayloadAction<TPpwrPackingTypeEnabledRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrResponseMessage = yield call(
			api.ppwr.updatePackingTypeEnabled,
			action.payload,
		);
		yield put(updatePpwrPackingTypeEnabledSuccess(action.payload));
		notificationContainer(response?.message ?? 'Packing type updated successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(updatePpwrPackingTypeEnabledSuccess(null));
	}
}

function* uploadPpwrProofDocumentWorker(action: PayloadAction<TPpwrUploadProofDocumentRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrProofDocument = yield call(api.ppwr.uploadProofDocument, action.payload);
		yield put(
			uploadPpwrProofDocumentSuccess({
				packingTypeId: action.payload.packingTypeId,
				document: response,
			}),
		);
		notificationContainer('File uploaded successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(uploadPpwrProofDocumentSuccess(null));
	}
}

function* deletePpwrProofDocumentWorker(action: PayloadAction<number>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrResponseMessage = yield call(api.ppwr.deleteProofDocument, action.payload);
		yield put(deletePpwrProofDocumentSuccess(action.payload));
		notificationContainer(response?.message ?? 'File deleted successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(deletePpwrProofDocumentSuccess(null));
	}
}

function* downloadPpwrProofDocumentWorker(
	action: PayloadAction<TPpwrDownloadProofDocumentRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: Blob = yield call(api.ppwr.downloadProofDocument, action.payload.documentId);
		yield put(downloadPpwrProofDocumentSuccess());
		if (response) {
			yield fileDownload(response, action.payload.fileName ?? 'file');
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData: string = yield error?.response?.data?.text?.() ?? '';
			let message = 'File download error';
			try {
				message = JSON.parse(responseData)?.message ?? message;
			} catch {
				// response body is not JSON
			}
			notificationContainer(message, 'error');
		}
		yield put(downloadPpwrProofDocumentSuccess());
	}
}

function* getPpwrCountryExclusionsWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrCountryExclusionsResponsePayload = yield call(
			api.ppwr.getCountryExclusions,
		);
		yield put(getPpwrCountryExclusionsSuccess(response));
	} catch (error) {
		showRequestError(error);
		yield put(getPpwrCountryExclusionsSuccess(null));
	}
}

function* createPpwrCountryExclusionWorker(action: PayloadAction<TPpwrCountryExclusionBody>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrCountryExclusion = yield call(
			api.ppwr.createCountryExclusion,
			action.payload,
		);
		yield put(savePpwrCountryExclusionSuccess(response));
		notificationContainer('Country exclusion created successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(savePpwrCountryExclusionSuccess(null));
	}
}

function* updatePpwrCountryExclusionWorker(
	action: PayloadAction<TPpwrUpdateCountryExclusionRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrCountryExclusion = yield call(
			api.ppwr.updateCountryExclusion,
			action.payload,
		);
		yield put(savePpwrCountryExclusionSuccess(response));
		notificationContainer('Country exclusion updated successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(savePpwrCountryExclusionSuccess(null));
	}
}

function* getPpwrManualReportingWorker(action: PayloadAction<TPpwrManualReportingRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrManualReportingResponsePayload = yield call(
			api.ppwr.getManualReporting,
			action.payload,
		);
		yield put(getPpwrManualReportingSuccess(response));
	} catch (error) {
		showRequestError(error);
		yield put(getPpwrManualReportingSuccess(null));
	}
}

function* upsertPpwrManualReportWorker(action: PayloadAction<TPpwrManualReportRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TPpwrResponseMessage = yield call(api.ppwr.upsertManualReport, action.payload);
		yield put(upsertPpwrManualReportSuccess(action.payload));
		yield put(getPpwrManualReportingRequest({ pageSize: 100 }));
		notificationContainer(response?.message ?? 'Report saved successfully', 'success');
	} catch (error) {
		showRequestError(error);
		yield put(upsertPpwrManualReportSuccess(null));
	}
}

export function* ppwrSaga() {
	yield takeEvery(getPpwrPackingTypesRequest.type, getPpwrPackingTypesWorker);
	yield takeEvery(updatePpwrPackingTypeWeightRequest.type, updatePpwrPackingTypeWeightWorker);
	yield takeEvery(updatePpwrPackingTypeEnabledRequest.type, updatePpwrPackingTypeEnabledWorker);
	yield takeEvery(uploadPpwrProofDocumentRequest.type, uploadPpwrProofDocumentWorker);
	yield takeEvery(deletePpwrProofDocumentRequest.type, deletePpwrProofDocumentWorker);
	yield takeEvery(downloadPpwrProofDocumentRequest.type, downloadPpwrProofDocumentWorker);
	yield takeEvery(getPpwrCountryExclusionsRequest.type, getPpwrCountryExclusionsWorker);
	yield takeEvery(createPpwrCountryExclusionRequest.type, createPpwrCountryExclusionWorker);
	yield takeEvery(updatePpwrCountryExclusionRequest.type, updatePpwrCountryExclusionWorker);
	yield takeEvery(getPpwrManualReportingRequest.type, getPpwrManualReportingWorker);
	yield takeEvery(upsertPpwrManualReportRequest.type, upsertPpwrManualReportWorker);
}
